import Vue from "vue";
import {get, mapValues, pickBy} from "lodash";
import axios from "@/modules/axios";
import Schema from "@/pages/order/schema";

const cleanState = () => ({
	data: {},
	schema: Schema(),
	sections: [Schema().basic]
});

const newSectionId = ({name}) => `${name}-${+ new Date()}`;

export default {
	namespaced: true,
	state: cleanState(),

	mutations: {
		field(state, {sectionId, fieldName, value}) {
			Vue.set(state.data, sectionId, state.data[sectionId] || {});
			Vue.set(state.data[sectionId], fieldName, value);
		},
		sectionData(state, {id, data}) {
			Vue.set(state.data, id, {...state.data[id], ...data});
		},
		sectionActive(state, {id, active}) {
			const index = state.sections.findIndex(x => x.id === id);
			const section = state.sections.find(x => x.id === id);
			Vue.set(state.sections, index, {...section, active});
		},
		sectionsActive(state, {active}) {
			state.sections.forEach(section => section.active = active);
		},
		addSection(state, {name}) {
			const section = state.schema.sections.find(x => x.name === name);
			state.sections.push({...section, active: true, id: newSectionId({name})});
		},
		removeSection(state, {id}) {
			state.sections = state.sections.filter(x => x.id !== id);
			delete state.data[id];
		},
		cloneSection(state, {name, id}) {
			const index = state.sections.findIndex(x => x.id === id);
			const section = state.schema.sections.find(x => x.name === name);
			const newId = newSectionId(section);

			state.sections.splice(index + 1, 0, {...section, active: true, id: newId});
			Vue.set(state.data, newId, {...state.data[id]});
		},
		resetState(state) {
			Object.entries(cleanState()).forEach(([key, val]) => state[key] = val);
		}
	},

	actions: {
		async send({getters, commit}) {
			const {data} = await axios.post("client/orders", getters.payload);
			commit("resetState");
			return data;
		}
	},

	getters: {
		data(state) {
			return state.data;
		},

		schema(state) {
			return state.schema;
		},

		sections(state) {
			return state.sections.reduce((array, section) => [...array, {
				...section,
				num: get(array.reverse().find(x => x.name === section.name), "num", 0) + 1,
				get title() {return `${section.name} (${this.num})`}
			}], []);
		},

		allSections(state, getters) {
			return getters.schema.sections;
		},

		payload(state, getters) {
			return {
				sections: getters.sections.map(x => ({
					title: x.title,
					fields: mapValues(getters.data[x.id], (val) => {
						if (val === true) return "ja";
						if (val === false) return "nej";
						return val;
					})
				}))
			}
		},

		noErrors(state, getters) {
			return Object.values(getters.errors).reduce((bool, obj) => Object.keys(obj).length === 0, false);
		},

		errors(state, getters) {
			const test = ({section, field}) => {
				const value = get(getters.data, [section.id, field.name], "");
				const validReq = field.required ? !!value : true;
				const validTest = field.test ? !!field.test.func(value) : true;
				const valid = validReq && validTest;
				return valid ? false : true;
			};

			const errors = getters.sections.reduce((obj, section) => {
				return {
					...obj,
					[section.id]: section.fields.reduce((fieldsObj, field) => {
						if (field.props.type === "group") {
							return field.props.fields.reduce((groupFieldsObj, field) => ({
								...fieldsObj,
								...groupFieldsObj,
								[field.name]: test({section, field})
							}), {});
						} else {
							return {
								...fieldsObj,
								[field.name]: test({section, field})
							};
						}
					}, {})
				}
			}, {});

			// remove properties without errors
			return mapValues(errors, (val) => pickBy(val, val => val));
		}
	}
};
